/**
 * Unified Listing Types for Mixed Property & Project Views
 * 
 * Generic types that allow themes to display properties and development projects
 * in the same listing view with different card treatments.
 * 
 * This is an OPT-IN capability - themes must explicitly use these types
 * and the mixed view pattern. Standard listing views continue to work as before.
 * 
 * Usage:
 * - Import `UnifiedListingItem` and transformation functions
 * - Transform your data using `transformPropertyToUnified` and `transformProjectToUnified`
 * - Render different card components based on `item.itemType`
 */

import type { Property } from '@/lib/api/clients/properties';
import type { PropertyDevelopmentProject } from '@/lib/api/clients/projects';
import { extractBestPriceSimple } from '@/lib/utils/price';

// =============================================================================
// UNIFIED ITEM TYPES
// =============================================================================

/**
 * Discriminator for unified listing items
 */
export type UnifiedItemType = 'property' | 'project';

/**
 * Common location structure shared across item types
 */
export interface UnifiedLocation {
  name: string | null;
  suburb: string | null;
  city: string | null;
  district: string | null;
  neighbourhood: string | null;
  region: string | null;
  country: string | null;
  countryCode: string | null;
}

/**
 * Unified item base - common fields across all item types
 */
interface UnifiedItemBase {
  id: string;
  itemType: UnifiedItemType;
  name: string;
  description: string | null;
  heroImageUrl: string | null;
  location: UnifiedLocation | null;
  displayPriceText: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Unified property item - represents a property in mixed views
 */
export interface UnifiedPropertyItem extends UnifiedItemBase {
  itemType: 'property';
  
  // Property-specific fields
  bedrooms: number | null;
  bathrooms: number | null;
  totalFloorArea: number | null;
  totalFloorAreaUnit: string | null;
  landArea: number | null;
  landAreaUnit: string | null;
  propertyType: {
    id: string;
    name: string;
  } | null;
  currency: string;
  currentAskingPrice: number | null;
  
  // Original data reference
  _originalProperty: Property;
}

/**
 * Unified project item - represents a development project in mixed views
 */
export interface UnifiedProjectItem extends UnifiedItemBase {
  itemType: 'project';
  
  // Project-specific fields
  slug: string | null;
  status: string | null;
  stage: string | null;
  projectType: string | null;
  totalUnits: number | null;
  soldUnits: number | null;
  availableUnits: number | null;
  unitMixSummary: string | null;
  estimatedCompletionDate: string | null;
  actualCompletionDate: string | null;
  marketingStatusText: string | null;
  
  // Original data reference
  _originalProject: PropertyDevelopmentProject;
}

/**
 * Union type for any unified listing item
 */
export type UnifiedListingItem = UnifiedPropertyItem | UnifiedProjectItem;

// =============================================================================
// TYPE GUARDS
// =============================================================================

/**
 * Type guard to check if item is a property
 */
export function isPropertyItem(item: UnifiedListingItem): item is UnifiedPropertyItem {
  return item.itemType === 'property';
}

/**
 * Type guard to check if item is a project
 */
export function isProjectItem(item: UnifiedListingItem): item is UnifiedProjectItem {
  return item.itemType === 'project';
}

// =============================================================================
// TRANSFORMATION FUNCTIONS
// =============================================================================

/**
 * Transform a Property to a UnifiedPropertyItem
 */
export function transformPropertyToUnified(property: Property): UnifiedPropertyItem {
  return {
    id: property.id,
    itemType: 'property',
    name: property.name,
    description: property.description ?? null,
    heroImageUrl: property.primaryImageUrl ?? null,
    location: property.location ? {
      name: property.location.name ?? null,
      suburb: property.location.suburb ?? null,
      city: property.location.city || property.location.localityName || null,
      district: property.location.district || property.location.administrativeAreaName || null,
      neighbourhood: property.location.neighbourhood ?? null,
      region: property.location.region ?? null,
      country: property.location.country || property.location.countryName || null,
      countryCode: property.location.countryCode ?? null,
    } : null,
    displayPriceText: null,
    createdAt: property.createdAt,
    updatedAt: property.updatedAt,
    
    // Property-specific
    bedrooms: property.bedrooms ?? null,
    bathrooms: property.bathrooms ?? null,
    totalFloorArea: property.totalFloorArea ?? null,
    totalFloorAreaUnit: property.totalFloorAreaUnit ?? null,
    landArea: property.landArea ?? null,
    landAreaUnit: property.landAreaUnit ?? null,
    propertyType: property.propertyType ? {
      id: property.propertyType.id,
      name: property.propertyType.name,
    } : null,
    currency: extractBestPriceSimple(property).currency,
    currentAskingPrice: extractBestPriceSimple(property).amount,
    
    _originalProperty: property,
  };
}

/**
 * Transform a PropertyDevelopmentProject to a UnifiedProjectItem
 */
export function transformProjectToUnified(project: PropertyDevelopmentProject): UnifiedProjectItem {
  return {
    id: project.id,
    itemType: 'project',
    name: project.name,
    description: project.description,
    heroImageUrl: project.heroImageUrl,
    location: null, // Projects don't have structured location in the API yet
    displayPriceText: project.displayPriceText,
    createdAt: project.createdAt,
    updatedAt: project.updatedAt,
    
    // Project-specific
    slug: project.slug,
    status: project.status,
    stage: project.stage,
    projectType: project.projectType,
    totalUnits: project.totalUnits,
    soldUnits: project.soldUnits,
    availableUnits: project.availableUnits,
    unitMixSummary: project.unitMixSummary,
    estimatedCompletionDate: project.estimatedCompletionDate,
    actualCompletionDate: project.actualCompletionDate,
    marketingStatusText: project.marketingStatusText,
    
    _originalProject: project,
  };
}

/**
 * Transform arrays of properties and projects into a unified list
 */
export function createUnifiedList(
  properties: Property[],
  projects: PropertyDevelopmentProject[]
): UnifiedListingItem[] {
  const unifiedProperties = properties.map(transformPropertyToUnified);
  const unifiedProjects = projects.map(transformProjectToUnified);
  
  return [...unifiedProperties, ...unifiedProjects];
}

// =============================================================================
// FILTER TYPES
// =============================================================================

/**
 * Filter value for item type filtering in mixed views
 */
export type ItemTypeFilter = 'all' | 'properties' | 'projects';

/**
 * Filter unified items by type
 */
export function filterByItemType(
  items: UnifiedListingItem[],
  filter: ItemTypeFilter
): UnifiedListingItem[] {
  if (filter === 'all') return items;
  if (filter === 'properties') return items.filter(isPropertyItem);
  if (filter === 'projects') return items.filter(isProjectItem);
  return items;
}
